import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthLayout, FieldError } from '@/components/auth/AuthLayout';
import { api } from '@/lib/customFetch';
import { useUser } from '@/contexts/UserContext';
import { mfaCodeSchema, type MfaCodeInput } from '@/lib/auth-schemas';

type LocationState = { pendingToken?: string };

export function MfaChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingToken = (location.state as LocationState | null)?.pendingToken;
  const { refresh } = useUser();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MfaCodeInput>({ resolver: zodResolver(mfaCodeSchema) });

  useEffect(() => {
    if (!pendingToken) {
      navigate('/login', { replace: true });
    }
  }, [pendingToken, navigate]);

  const onSubmit = handleSubmit(async ({ code }) => {
    if (!pendingToken) return;
    try {
      await api.post(
        '/auth/mfa/verify-pending',
        { code },
        { headers: { Authorization: `Bearer ${pendingToken}` } },
      );
      toast.success('Autenticado.');
      await refresh();
      navigate('/', { replace: true });
    } catch {
      // toast já mostrado por customFetch — pendingToken segue válido até expirar (5min)
    }
  });

  if (!pendingToken) return null;

  return (
    <AuthLayout eyebrow="Verificação em duas etapas">
      <h2 style={{ fontSize: 26, fontWeight: 600, marginBottom: 8 }}>Verificação MFA</h2>
      <p style={{ color: 'var(--ev-fg-muted)', fontSize: 14, marginBottom: 28 }}>
        Informe o código atual do seu autenticador.
      </p>

      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 22 }}>
          <Label htmlFor="code" style={{ display: 'block', marginBottom: 6 }}>Código de 6 dígitos</Label>
          <Input
            id="code"
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            {...register('code')}
          />
          {errors.code && <FieldError>{errors.code.message}</FieldError>}
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          Verificar
        </Button>
      </form>
    </AuthLayout>
  );
}
